<script setup lang="ts">
/**
 * The one inquiry dialog of the portal (UC-30, ADR-093, AP-F6.5), opened through
 * `useInquiryDialog()`: `InquiryForm` from `packages/ui` with the kinds, the preselected kind and
 * the entry it was opened with, the catalogue as products to pick, and the typed client as the way
 * to the API. The head follows the design prototype (B-124): the team's star, the title of the
 * chosen kind or of the entry, "Geht direkt an das Team."; the intro of the kind or entry stands
 * below the kinds' segment and is read with the dialog, as its description is (UP5, e.g. why a
 * login was refused), and for a customer the box "Anfrage als" with name, e-mail address and
 * customer from the session, as whom the API sends it (B-125, ADR-093). Titles and intros come from
 * `packages/ui` (`inquiryHeading`). The privacy sentence links to `/datenschutz` (B-131; the page
 * itself follows with B-122). "Abbrechen" closes; a failed send says what `useInquirySend` hands
 * back. After sending the form thanks and the dialog offers "Fertig". The dialog
 * binds the kind (`v-model:kind`), so title and segment always show the same. A guest gets no
 * products to pick while the guest view is off (ADR-133 point 3): the catalogue is not theirs to
 * read then. Opened with `APPLICATION` it shows the application for the beta instead
 * (`ApplicationForm`, ADR-143): the title "Für die Beta bewerben", its intro above the form, the
 * same sending. Since the application page `/beta` (ADR-143 addendum 3) no entry opens it; it
 * stays for when the design changes back (TL 2026-10-28).
 */
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import ApplicationForm from "@ai-platform/ui/ApplicationForm.vue";
import { applicationHeading } from "@ai-platform/ui/application.ts";
import { inquiryHeading, type FormInquiryKind } from "@ai-platform/ui/inquiry.ts";
import InquiryForm from "@ai-platform/ui/InquiryForm.vue";
import PixButton from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixButton/PixButton.vue";
import { useProducts } from "../composables/customer.ts";
import { useInquiryDialog, useInquirySend } from "../composables/inquiry.ts";
import { useSession } from "../session/session.ts";
import { usePortalSettings } from "../session/settings.ts";
import AppDialog from "@ai-platform/ui/AppDialog.vue";
import TeamAvatar from "@ai-platform/ui/TeamAvatar.vue";

const { request, openings, close } = useInquiryDialog();
const send = useInquirySend();
const { session } = useSession();
const settings = usePortalSettings();
const { data: catalogue } = useProducts({ enabled: () => session.value !== null || settings.value.guestCatalog });

const { t } = useI18n();
const products = computed(() => catalogue.value?.items.map((product) => ({ id: product.id, name: product.name })));

/** The inquiry form's request; null while closed or for the application. */
const inquiryRequest = computed(() => {
  const opened = request.value;
  return opened && !("form" in opened) ? opened : null;
});
/** The application's request; null while closed or for the inquiry form. */
const applicationRequest = computed(() => {
  const opened = request.value;
  return opened && "form" in opened ? opened : null;
});

/** The kind on screen: the preselected one when it is offered, otherwise the first; then the sender's choice. */
const kind = ref<FormInquiryKind | null>(null);
watch(
  [openings, inquiryRequest],
  () => {
    const opened = inquiryRequest.value;
    kind.value = opened ? (opened.kind && opened.kinds.includes(opened.kind) ? opened.kind : opened.kinds[0]) : null;
  },
  { immediate: true },
);

/** As whom a customer writes; a guest fills in name and address in the form instead. */
const sender = computed(() => {
  const current = session.value;
  if (!current?.customer || kind.value === "access") return null;
  return { name: current.user.displayName, email: current.user.email, customer: current.customer.name };
});

const heading = computed(() => {
  if (applicationRequest.value) return applicationHeading(t, applicationRequest.value.lead);
  return inquiryRequest.value && kind.value ? inquiryHeading([kind.value], t, inquiryRequest.value.lead) : null;
});

const sent = ref(false);
watch(openings, () => (sent.value = false));

/** The intro is read with the dialog while the form shows it; after sending it is gone. */
const introId = useId();
const describedBy = computed(() => (heading.value && !sent.value ? introId : undefined));
</script>

<template>
  <AppDialog
    :open="request !== null"
    :title="heading?.title ?? ''"
    description="Geht direkt an das Team."
    :described-by="describedBy"
    :width="applicationRequest ? 'form' : 'wide'"
    @close="close"
  >
    <template #icon>
      <TeamAvatar />
    </template>
    <template v-if="applicationRequest">
      <p v-if="heading && !sent" :id="introId" class="text-ui text-text-body-2 m-0 mb-5" data-inquiry-intro>
        {{ heading.intro }}
      </p>
      <ApplicationForm
        :key="openings"
        :send="send"
        privacy-href="/datenschutz"
        cancelable
        @cancel="close"
        @sent="sent = true"
      />
    </template>
    <InquiryForm
      v-else-if="inquiryRequest"
      :key="openings"
      :kinds="inquiryRequest.kinds"
      :kind="kind ?? undefined"
      :products="products"
      :product-id="inquiryRequest.productId"
      :lead="inquiryRequest.lead"
      :send="send"
      privacy-href="/datenschutz"
      cancelable
      @update:kind="kind = $event ?? null"
      @cancel="close"
      @sent="sent = true"
    >
      <template #intro>
        <p v-if="heading" :id="introId" class="text-ui text-text-body-2 m-0" data-inquiry-intro>
          {{ heading.intro }}
        </p>
        <p
          v-if="sender"
          class="rounded-control bg-fill text-ui text-text-body m-0 flex items-start gap-2.5 px-3.5 py-3"
          data-inquiry-sender
        >
          <!-- Phosphor Duotone "user", as in the prototype -->
          <svg
            class="text-text-soft mt-0.5 size-4 flex-none"
            viewBox="0 0 256 256"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M192 96a64 64 0 1 1-64-64a64 64 0 0 1 64 64" opacity="0.2" />
            <path
              d="M230.92 212c-15.23-26.33-38.7-45.21-66.09-54.16a72 72 0 1 0-73.66 0c-27.39 8.94-50.86 27.82-66.09 54.16a8 8 0 1 0 13.85 8c18.84-32.56 52.14-52 89.07-52s70.23 19.44 89.07 52a8 8 0 1 0 13.85-8M72 96a56 56 0 1 1 56 56a56.06 56.06 0 0 1-56-56"
            />
          </svg>
          <span>
            Anfrage als
            <strong v-if="sender.name" class="text-text-head">{{ sender.name }}</strong>
            <template v-if="sender.name"> · </template>{{ sender.email }} · {{ sender.customer }}
          </span>
        </p>
      </template>
    </InquiryForm>
    <!-- "Fertig" as in the prototype (`btn-solid`): the solid surface, 40 pixels (44 on touch
         screens), full width on phones -->
    <div v-if="sent" class="mt-5 flex justify-end max-sm:flex-col">
      <PixButton
        class="text-ui min-h-(--size-field) px-4 font-semibold [--pix-button-radius:var(--radius-control)] pointer-coarse:min-h-(--size-button)"
        data-action="close"
        @click="close"
      >
        Fertig
      </PixButton>
    </div>
  </AppDialog>
</template>
